/**
 * CR307/CR384/CR508: S0 Scan Settings — Flyer Scan tab
 * Controls: flyer capture mode, max pages, S1.4 Locator/Cropper prompt packs.
 */

import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { FileText, Info, Download, HardDrive, Crosshair, Crop, SlidersHorizontal, Diamond, Shield } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FLAGS } from '@/lib/featureFlags';
import { FLYER_CAPTURE_MODES, type FlyerCaptureMode } from '../types';
import type { AppScanSettings } from '@/hooks/useScanSettings';
import { supabase } from '@/integrations/supabase/client';

interface WorkflowPack {
  id: string;
  name: string;
  usage_stage: string | null;
}

interface FlyerScanTabProps {
  settings: AppScanSettings;
  updateField: <K extends keyof AppScanSettings>(key: K, value: AppScanSettings[K]) => void;
  disabled?: boolean;
  flyerCaptureMode?: FlyerCaptureMode;
  onFlyerCaptureModeChange?: (mode: FlyerCaptureMode) => void;
}

export function FlyerScanTab({
  settings,
  updateField,
  disabled = false,
  flyerCaptureMode = 'DOWNLOAD_ONLY',
  onFlyerCaptureModeChange,
}: FlyerScanTabProps) {
  const showImageModes = useFeatureFlag(FLAGS.FLYER_CAPTURE_IMAGE_MODES);
  const [locatorPacks, setLocatorPacks] = useState<WorkflowPack[]>([]);
  const [cropperPacks, setCropperPacks] = useState<WorkflowPack[]>([]);

  // CR508: Load workflow prompt packs for S1.4
  useEffect(() => {
    const loadPacks = async () => {
      const { data } = await supabase
        .from('prompt_packs')
        .select('id, name, usage_stage')
        .eq('pack_kind', 'WORKFLOW')
        .is('archived_at', null)
        .in('usage_stage', ['S1_4_LOCATOR', 'S1_4_CROPPER'])
        .order('name');
      if (data) {
        setLocatorPacks(data.filter(p => p.usage_stage === 'S1_4_LOCATOR'));
        setCropperPacks(data.filter(p => p.usage_stage === 'S1_4_CROPPER'));
      }
    };
    loadPacks();
  }, []);

  const clampMaxPagesPerFlyer = (val: number) => {
    if (isNaN(val)) return 50;
    return Math.max(0, Math.min(5000, val));
  };
  const clampMaxTotalPages = (val: number) => {
    if (isNaN(val)) return 1250;
    return Math.max(0, Math.min(100000, val));
  };

  return (
    <div className="space-y-6">
      {/* CR384: Flyer Capture Mode */}
      {onFlyerCaptureModeChange && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Download className="h-4 w-4" />
            Flyer Capture Mode
          </div>

          <Select
            value={flyerCaptureMode}
            onValueChange={(v) => onFlyerCaptureModeChange(v as FlyerCaptureMode)}
            disabled={disabled}
          >
            <SelectTrigger className="h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FLYER_CAPTURE_MODES.map((mode) => {
                const hidden = mode.comingSoon && !showImageModes;
                if (hidden) return null;
                return (
                  <SelectItem
                    key={mode.value}
                    value={mode.value}
                    disabled={mode.comingSoon}
                    className="text-xs"
                  >
                    {mode.label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          <p className="text-[11px] text-muted-foreground">
            {FLYER_CAPTURE_MODES.find(m => m.value === flyerCaptureMode)?.helper}
          </p>

          <div className="mt-3 p-2.5 bg-muted/30 rounded-md border border-border/40">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground mb-1">
              <HardDrive className="h-3 w-3" />
              Rendered Page Storage
            </div>
            <p className="text-xs font-mono text-foreground/80">Bucket: flyer-fetch-assets</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Object keys vary by capture mode. The exact key is stored in <span className="font-mono">render_storage_path</span>.
            </p>
          </div>
        </div>
      )}

      {!onFlyerCaptureModeChange && (
        <div className="p-2.5 bg-muted/30 rounded-md border border-border/40">
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground mb-1">
            <HardDrive className="h-3 w-3" />
            Rendered Page Storage
          </div>
          <p className="text-xs font-mono text-foreground/80">Bucket: flyer-fetch-assets</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Object keys vary by capture mode. The exact key is stored in <span className="font-mono">render_storage_path</span>.
          </p>
        </div>
      )}

      {/* CR508: S1.4 Snippet Locator / Cropper Prompt Packs */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Crosshair className="h-4 w-4" />
          S1.5 — Locate & Crop
        </div>

        <div className="space-y-2">
          <Label className="text-xs">Locator Prompt Pack</Label>
          <Select
            value={settings.s1_4_locator_prompt_pack_id || '_none'}
            onValueChange={(v) => updateField('s1_4_locator_prompt_pack_id', v === '_none' ? null : v)}
            disabled={disabled}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Select locator pack…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none" className="text-xs text-muted-foreground">None (deterministic mode)</SelectItem>
              {locatorPacks.map(p => (
                <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground">
            AI prompt pack used for deal snippet localization. Falls back to deterministic mode if unset.
          </p>
        </div>

        {/* Cropper (mandatory Pass B) */}
        <div className="space-y-3 pt-3 border-t border-border/30">
          <div className="flex items-center gap-2">
            <Crop className="h-4 w-4 text-muted-foreground" />
            <Label className="text-xs font-medium">Cropper Refinement (Pass B)</Label>
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Required</Badge>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-xs">
                  Mandatory second AI pass that refines the snippet crop for tighter, deal-specific bounding boxes.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Cropper Prompt Pack</Label>
            <Select
              value={settings.s1_4_cropper_prompt_pack_id || '_none'}
              onValueChange={(v) => updateField('s1_4_cropper_prompt_pack_id', v === '_none' ? null : v)}
              disabled={disabled}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select cropper pack…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none" className="text-xs text-muted-foreground">None (will block S1.5)</SelectItem>
                {cropperPacks.map(p => (
                  <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!settings.s1_4_cropper_prompt_pack_id && (
              <div className="p-2 bg-amber-50/50 dark:bg-amber-900/20 border border-amber-300/40 rounded text-xs text-amber-800 dark:text-amber-200">
                ⚠ Cropper pack is required. S1.5 will fail with PROMPT_PACK_NOT_CONFIGURED if unset.
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              AI prompt pack for the mandatory cropper refinement pass (Pass B).
            </p>
          </div>

          {/* CR555-P6: Cropper Model Override */}
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5">
              Cropper Model
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">
                    Override the AI model used for Pass B (cropper) without affecting the locator model.
                    Use a cheaper/faster model to reduce costs.
                  </p>
                </TooltipContent>
              </Tooltip>
            </Label>
            <Select
              value={settings.s1_4_cropper_model_mode === 'override' && settings.s1_4_cropper_model_hint
                ? settings.s1_4_cropper_model_hint
                : '_run_model'}
              onValueChange={(v) => {
                if (v === '_run_model') {
                  updateField('s1_4_cropper_model_mode', 'use_run_model');
                  updateField('s1_4_cropper_model_hint', null);
                } else if (v === '_custom') {
                  updateField('s1_4_cropper_model_mode', 'override');
                  // Keep existing hint or set empty
                } else {
                  updateField('s1_4_cropper_model_mode', 'override');
                  updateField('s1_4_cropper_model_hint', v);
                }
              }}
              disabled={disabled}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select model…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_run_model" className="text-xs">Use run model (default)</SelectItem>
                <SelectItem value="google/gemini-2.5-flash" className="text-xs">Gemini 2.5 Flash (cheap vision)</SelectItem>
                <SelectItem value="google/gemini-2.5-flash-lite" className="text-xs">Gemini 2.5 Flash Lite (cheapest)</SelectItem>
                <SelectItem value="google/gemini-3-flash-preview" className="text-xs">Gemini 3 Flash Preview</SelectItem>
                <SelectItem value="google/gemini-2.5-pro" className="text-xs">Gemini 2.5 Pro (highest quality)</SelectItem>
                <SelectItem value="_custom" className="text-xs">Custom…</SelectItem>
              </SelectContent>
            </Select>
            {settings.s1_4_cropper_model_mode === 'override' && settings.s1_4_cropper_model_hint === null && (
              <Input
                className="h-8 text-xs font-mono"
                placeholder="e.g. google/gemini-2.5-flash"
                value=""
                onChange={(e) => updateField('s1_4_cropper_model_hint', e.target.value || null)}
                disabled={disabled}
              />
            )}
            {settings.s1_4_cropper_model_mode === 'override' && (
              <p className="text-[10px] text-muted-foreground">
                Pass B will use <span className="font-mono font-medium">{settings.s1_4_cropper_model_hint || '(not set)'}</span> instead of the run model.
              </p>
            )}
          </div>

          {/* CR534: Confidence Thresholds */}
          <div className="space-y-4 pt-3 border-t border-border/30">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
              <Label className="text-xs font-medium">Confidence Thresholds</Label>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">Advanced</Badge>
            </div>

            <div className="space-y-3">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1.5">
                    Cropper Min Confidence
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs text-xs">
                          Minimum confidence for Pass B (cropper) to accept a refined bbox.
                          Lower = more crops, possibly sloppier. Temporary while tuning.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <span className="text-xs font-mono text-foreground/80">
                    {settings.s1_4_min_cropper_confidence.toFixed(2)}
                  </span>
                </div>
                <Slider
                  value={[settings.s1_4_min_cropper_confidence]}
                  onValueChange={([v]) => updateField('s1_4_min_cropper_confidence', parseFloat(v.toFixed(2)))}
                  min={0}
                  max={1}
                  step={0.05}
                  disabled={disabled}
                  className="w-full"
                />
                <p className="text-[10px] text-muted-foreground">
                  Default: 0.50. Range: 0.0–1.0. Step: 0.05.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1.5">
                    Locator Min Confidence
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs text-xs">
                          Minimum confidence for Pass A (locator) to accept a bbox.
                          Lower = more candidates passed to cropper.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <span className="text-xs font-mono text-foreground/80">
                    {settings.s1_4_min_locator_confidence.toFixed(2)}
                  </span>
                </div>
                <Slider
                  value={[settings.s1_4_min_locator_confidence]}
                  onValueChange={([v]) => updateField('s1_4_min_locator_confidence', parseFloat(v.toFixed(2)))}
                  min={0}
                  max={1}
                  step={0.05}
                  disabled={disabled}
                  className="w-full"
                />
                <p className="text-[10px] text-muted-foreground">
                  Default: 0.80. Range: 0.0–1.0. Step: 0.05.
                </p>
              </div>

              {/* CR540: Max Crop Area Ratio */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1.5">
                    Max Crop Area Ratio
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs text-xs">
                          Reject any crop bbox larger than this fraction of the full page.
                          Lower = tighter crops only. Default 0.55.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </Label>
                  <span className="text-xs font-mono text-foreground/80">
                    {settings.s1_4_max_crop_area_ratio.toFixed(2)}
                  </span>
                </div>
                <Slider
                  value={[settings.s1_4_max_crop_area_ratio]}
                  onValueChange={([v]) => updateField('s1_4_max_crop_area_ratio', parseFloat(v.toFixed(2)))}
                  min={0.10}
                  max={1.0}
                  step={0.05}
                  disabled={disabled}
                  className="w-full"
                />
                <p className="text-[10px] text-muted-foreground">
                  Default: 0.55. Range: 0.10–1.0. Step: 0.05.
                </p>
              </div>

              {/* CR540: Prefer Pass A when Pass B worse */}
              <div className="flex items-center justify-between pt-2">
                <Label className="text-xs flex items-center gap-1.5">
                  Prefer Pass A when Pass B worse
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs text-xs">
                        CR539 guardrail: revert to Pass A (Locator) bbox when Pass B (Cropper)
                        produces worse evidence or drifts to wrong region.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </Label>
                <Switch
                  checked={settings.s1_4_prefer_pass_a_when_worse}
                  onCheckedChange={(v) => updateField('s1_4_prefer_pass_a_when_worse', v)}
                  disabled={disabled}
                />
              </div>

              {/* CR543: OCR Hint Settings */}
              <div className="space-y-3 pt-3 border-t border-border/30">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-xs font-medium">OCR Hint for Cropper</Label>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">CR543</Badge>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs flex items-center gap-1.5">
                      OCR Hint Margin
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs text-xs">
                            How much to expand the Pass A bbox (as a fraction) to extract OCR text
                            that is passed to the cropper as a hint. Larger = more context but noisier.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </Label>
                    <span className="text-xs font-mono text-foreground/80">
                      {settings.s1_4_ocr_hint_margin.toFixed(2)}
                    </span>
                  </div>
                  <Slider
                    value={[settings.s1_4_ocr_hint_margin]}
                    onValueChange={([v]) => updateField('s1_4_ocr_hint_margin', parseFloat(v.toFixed(2)))}
                    min={0}
                    max={0.30}
                    step={0.02}
                    disabled={disabled}
                    className="w-full"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Default: 0.08. Range: 0.00–0.30. Step: 0.02.
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs flex items-center gap-1.5">
                      OCR Hint Max Chars
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs text-xs">
                            Maximum characters of OCR text to include as a hint for the cropper prompt.
                            Too long may dilute the signal; too short may miss context.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </Label>
                    <span className="text-xs font-mono text-foreground/80">
                      {settings.s1_4_ocr_hint_max_chars}
                    </span>
                  </div>
                  <Slider
                    value={[settings.s1_4_ocr_hint_max_chars]}
                    onValueChange={([v]) => updateField('s1_4_ocr_hint_max_chars', Math.round(v))}
                    min={100}
                    max={2000}
                    step={50}
                    disabled={disabled}
                    className="w-full"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Default: 500. Range: 100–2000. Step: 50.
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs flex items-center gap-1.5">
                      Crop Decode Target (MP)
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs text-xs">
                            S1.5 target megapixels for page decode before crop prep. Higher keeps more detail but uses more memory.
                            Larger pages are downscaled toward this target.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </Label>
                    <span className="text-xs font-mono text-foreground/80">
                      {settings.s1_4_crop_decode_target_mp}
                    </span>
                  </div>
                  <Slider
                    value={[settings.s1_4_crop_decode_target_mp]}
                    onValueChange={([v]) => updateField('s1_4_crop_decode_target_mp', Math.round(v))}
                    min={8}
                    max={24}
                    step={1}
                    disabled={disabled}
                    className="w-full"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Default: 12. Range: 8–24 MP.
                  </p>
                </div>

                <div className="space-y-2 p-3 rounded-md border border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-800/60">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs flex items-center gap-1.5">
                      Oversize Guard (Fallback Only)
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs text-xs">
                            When enabled, S1.5 skips Pass B/crop for pages above the max MP and writes PAGE_FALLBACK directly.
                            Use this to avoid memory-heavy crop attempts on very large pages.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </Label>
                    <Switch
                      checked={settings.s1_4_oversize_guard_enabled}
                      onCheckedChange={(checked) => updateField('s1_4_oversize_guard_enabled', checked)}
                      disabled={disabled}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label className="text-xs flex items-center gap-1.5">
                      Max Crop Input (MP)
                      <Badge variant="outline" className="text-[9px] px-1 py-0">S1.5 Guard</Badge>
                    </Label>
                    <span className="text-xs font-mono text-foreground/80">
                      {settings.s1_4_oversize_guard_max_mp}
                    </span>
                  </div>
                  <Slider
                    value={[settings.s1_4_oversize_guard_max_mp]}
                    onValueChange={([v]) => updateField('s1_4_oversize_guard_max_mp', Math.round(v))}
                    min={4}
                    max={40}
                    step={1}
                    disabled={disabled || !settings.s1_4_oversize_guard_enabled}
                    className="w-full"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Default: 12. If a candidate page exceeds this size, S1.5 records PAGE_FALLBACK and skips cropper/crop.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Flyer Scan Defaults */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <FileText className="h-4 w-4" />
          Flyer Scan Defaults
        </div>

        <p className="text-xs text-muted-foreground">
          Page caps that apply when scanning flyer PDFs. These prevent runaway scans on very large documents.
        </p>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5">
              Max Pages Per Flyer
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">
                    Limits how many pages can be scanned from any single flyer PDF.
                    Pages beyond this limit are skipped. Set to 0 for no limit. Range: 0–5,000.
                  </p>
                </TooltipContent>
              </Tooltip>
            </Label>
            <Input
              type="number"
              min={0}
              max={5000}
              value={settings.flyer_max_pages_per_flyer}
              onChange={(e) => updateField('flyer_max_pages_per_flyer', clampMaxPagesPerFlyer(parseInt(e.target.value)))}
              onBlur={(e) => updateField('flyer_max_pages_per_flyer', clampMaxPagesPerFlyer(parseInt(e.target.value)))}
              disabled={disabled}
              className="h-8 text-xs"
            />
            <p className="text-xs text-muted-foreground">
              Default: 50. Set to 0 for no limit (debug mode).
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5">
              Max Total Pages Per Run
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">
                    Limits total pages scanned across all flyers for the entire run.
                    Once reached, remaining pages are skipped. Set to 0 for no limit. Range: 0–100,000.
                  </p>
                </TooltipContent>
              </Tooltip>
            </Label>
            <Input
              type="number"
              min={0}
              max={100000}
              step={100}
              value={settings.flyer_max_total_pages_per_run}
              onChange={(e) => updateField('flyer_max_total_pages_per_run', clampMaxTotalPages(parseInt(e.target.value)))}
              onBlur={(e) => updateField('flyer_max_total_pages_per_run', clampMaxTotalPages(parseInt(e.target.value)))}
              disabled={disabled}
              className="h-8 text-xs"
            />
            <p className="text-xs text-muted-foreground">
              Default: 1,000. Set to 0 for no limit (debug mode).
            </p>
          </div>
        </div>
      </div>

      {/* CR544: Diamond Mode Controls */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Diamond className="h-4 w-4" />
          CR544: Diamond Mode
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">Iteration Controls</Badge>
        </div>

        <p className="text-xs text-muted-foreground">
          Controls how S1.5 handles reruns. Diamond Mode always writes new attempt outputs for measurable iteration.
          Stable Mode uses idempotent caching once quality stabilizes.
        </p>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5">
              Run Policy
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">
                    Diamond: every run produces new attempt-scoped artifacts. Stable: skip if already located (idempotent).
                  </p>
                </TooltipContent>
              </Tooltip>
            </Label>
            <Select
              value={settings.s1_4_run_policy}
              onValueChange={(v) => updateField('s1_4_run_policy', v as 'diamond' | 'stable')}
              disabled={disabled}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="diamond" className="text-xs">
                  <span className="flex items-center gap-1.5">
                    <Diamond className="h-3 w-3" /> Diamond (always new attempt)
                  </span>
                </SelectItem>
                <SelectItem value="stable" className="text-xs">
                  <span className="flex items-center gap-1.5">
                    <Shield className="h-3 w-3" /> Stable (cached / idempotent)
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5">
              Recompute Policy
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">
                    FULL: recompute everything. COPY_FORWARD_IF_UNCHANGED: fast iteration. LOCATE_ONLY / CROP_ONLY: partial rerun.
                  </p>
                </TooltipContent>
              </Tooltip>
            </Label>
            <Select
              value={settings.s1_4_recompute_policy}
              onValueChange={(v) => updateField('s1_4_recompute_policy', v as any)}
              disabled={disabled}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="FULL" className="text-xs">FULL (recompute)</SelectItem>
                <SelectItem value="COPY_FORWARD_IF_UNCHANGED" className="text-xs">Copy forward if unchanged</SelectItem>
                <SelectItem value="LOCATE_ONLY" className="text-xs">Locate only (skip crop)</SelectItem>
                <SelectItem value="CROP_ONLY" className="text-xs">Crop only (reuse bbox)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Separator className="my-2" />

        {/* Phase 5: Retention */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs flex items-center gap-1.5">
              Max Attempts Kept
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">
                    Keep the last N S1.5 attempts per batch run. Older attempts are eligible for purge.
                  </p>
                </TooltipContent>
              </Tooltip>
            </Label>
            <span className="text-xs font-mono text-foreground/80">
              {settings.s1_4_max_attempts_kept}
            </span>
          </div>
          <Slider
            value={[settings.s1_4_max_attempts_kept]}
            onValueChange={([v]) => updateField('s1_4_max_attempts_kept', Math.round(v))}
            min={1}
            max={50}
            step={1}
            disabled={disabled}
            className="w-full"
          />
          <p className="text-[10px] text-muted-foreground">
            Default: 10. Range: 1–50.
          </p>
        </div>
      </div>

      {/* Run Scope Info (read-only) */}
      <div className="space-y-3 pt-2 border-t border-border/50">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <Info className="h-3 w-3" />
          Run Scope (Read-Only)
        </div>

        <div className="p-3 bg-muted/30 rounded-lg text-xs text-muted-foreground">
          <p className="mb-2">
            The following parameters are set during batch run creation via the <strong>Batch Wizard</strong> and cannot be edited here:
          </p>
          <ul className="list-disc list-inside space-y-0.5 ml-1">
            <li>Sources enabled (FLYER, WEBSITE_PROMPT_PACK, etc.)</li>
            <li>Merchants in scope</li>
            <li>Flyer mode and bucket source</li>
          </ul>
          <p className="mt-2 text-muted-foreground/70 italic">
            To change these, create a new batch run using the wizard.
          </p>
        </div>
      </div>
    </div>
  );
}
